import { createContext, useContext } from "react";
import type { Setting, Situation } from "../settings/types";

/**
 * ═══ LA SITUATION AFFICHÉE SUR L'ÉCRAN DE CONFIRMATION ═══
 *
 * La confirmation a cinq versions selon la réponse du participant : sur place, à
 * distance, représenté, paiement en attente, ne participe pas. L'organisateur n'en
 * voyait qu'une, la sienne, et réglait les quatre autres à l'aveugle : leurs titres,
 * leurs messages et leurs emails étaient empilés dans une seule famille du panneau.
 *
 * Un sélecteur, en tête de « Cette page » dans le panneau, choisit la version. L'écran la rend (`routes/Confirmation`),
 * et le panneau ne montre que les réglages qui s'y voient (`situations` dans le
 * catalogue). C'est une VUE, comme « Vu par » : rien ne s'écrit, et un participant n'en
 * sait rien. Hors du mode organisateur, la situation vaut `null` et l'écran lit la vraie
 * réponse.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */
export const SITUATIONS: ReadonlyArray<{ value: Situation; label: string }> = [
  { value: "onsite", label: "Sur place" },
  { value: "remote", label: "À distance" },
  { value: "represented", label: "Se fait représenter" },
  { value: "pending", label: "Paiement en attente" },
  { value: "decline", label: "Ne participe pas" },
];

/** La situation montrée par défaut : celle de la plupart des participants. */
export const DEFAULT_SITUATION: Situation = "onsite";

type Control = {
  /** `null` hors du mode organisateur : l'écran lit alors la vraie réponse. */
  situation: Situation | null;
  setSituation: (situation: Situation) => void;
};

const Context = createContext<Control>({
  situation: null,
  setSituation: () => undefined,
});

export const SituationProvider = Context.Provider;

export function useSituation(): Situation | null {
  return useContext(Context).situation;
}

export function useSituationControl(): Control {
  return useContext(Context);
}

/** Le réglage se voit-il dans cette situation ? Sans situation choisie, toujours. */
export function shownIn(
  setting: Setting,
  situation: Situation | null,
): boolean {
  return !situation || !setting.situations || setting.situations.includes(situation);
}
